export type BehaviorId = string;
export type BTNodeId = string;
export type IkOrientationMode = "free" | "keepDown" | "alignWithGrasp";

/** User-friendly name shown on node blocks and editable in the inspector. */
export type WithLabel = { label?: string };

export type BTComposite = WithLabel &
  (
    | { id: BTNodeId; kind: "sequence"; children: BTNodeId[] }
    | { id: BTNodeId; kind: "selector"; children: BTNodeId[] }
    | { id: BTNodeId; kind: "retry"; maxAttempts: number; child: BTNodeId | null }
    | { id: BTNodeId; kind: "repeat"; count: number; child: BTNodeId | null }
    | {
        id: BTNodeId;
        kind: "ifThenElse";
        conditionChild: BTNodeId | null;
        thenChild: BTNodeId | null;
        elseChild: BTNodeId | null;
      }
  );

export type BTLeaf = WithLabel &
  (
    | {
        id: BTNodeId;
        kind: "servoPickPlace";
        pickBodyUuid: string;
        dropBodyUuid: string;
        endEffectorRef: string;
      }
    | {
        id: BTNodeId;
        kind: "moveEeToXyz";
        x: number;
        y: number;
        z: number;
        /** Optional world-body anchor. When set, XYZ are interpreted as offsets from this body. */
        targetBodyUuid?: string;
        settleSeconds: number;
        orientationMode?: IkOrientationMode;
        orientationStrength?: number;
        endEffectorRef: string;
      }
    | {
        id: BTNodeId;
        kind: "moveEeByOffset";
        dx: number;
        dy: number;
        dz: number;
        settleSeconds: number;
        orientationMode?: IkOrientationMode;
        orientationStrength?: number;
        endEffectorRef: string;
      }
    | {
        id: BTNodeId;
        kind: "runTrainedModel";
        modelId: string;
        durationSeconds: number;
        commandVx: number;
        commandWz: number;
      }
    | { id: BTNodeId; kind: "openGripper"; settleSeconds: number }
    | { id: BTNodeId; kind: "closeGripper"; settleSeconds: number }
    | { id: BTNodeId; kind: "goToKeyframe"; keyframeName: string }
    | { id: BTNodeId; kind: "wait"; seconds: number }
    | {
        id: BTNodeId;
        kind: "randomizeObjectPose";
        bodyUuid: string;
        radiusXY: number;
        yawRangeDeg: number;
      }
    | {
        id: BTNodeId;
        kind: "setObjectPose";
        bodyUuid: string;
        x: number;
        y: number;
        z: number;
        qw: number;
        qx: number;
        qy: number;
        qz: number;
      }
    | { id: BTNodeId; kind: "resetToBaseline" }
    | { id: BTNodeId; kind: "markSuccess" }
    | { id: BTNodeId; kind: "markFailure" }
    | { id: BTNodeId; kind: "conditionObjectGrasped"; bodyUuid: string; minHeightY: number }
    | {
        id: BTNodeId;
        kind: "conditionEeNear";
        endEffectorRef: string;
        x: number;
        y: number;
        z: number;
        /** Optional world-body anchor. When set, XYZ are interpreted as offsets from this body. */
        targetBodyUuid?: string;
        toleranceM: number;
      }
    | {
        id: BTNodeId;
        kind: "conditionObjectNear";
        bodyUuid: string;
        x: number;
        y: number;
        z: number;
        /** Optional world-body anchor. When set, XYZ are interpreted as offsets from this body. */
        targetBodyUuid?: string;
        toleranceM: number;
      }
    | { id: BTNodeId; kind: "conditionTimeElapsed"; seconds: number }
  );

export type BTNode = BTComposite | BTLeaf;

export interface BehaviorDocument {
  id: BehaviorId;
  name: string;
  rootId: BTNodeId;
  nodes: Record<BTNodeId, BTNode>;
  /** Captured via "Capture baseline" for ResetToBaseline nodes. */
  baselineSnapshot: unknown | null;
  /** Built-in templates are read-only until duplicated. */
  isTemplate?: boolean;
}

export function createNodeId(): BTNodeId {
  const c = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (c?.randomUUID) return `n_${c.randomUUID()}`;
  return `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}
